import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { extractEdgeFunctionError } from "@/lib/edgeFunctionError";

interface ImpersonationProfile {
  user_id: string;
  full_name: string | null;
  email?: string;
  avatar_url?: string | null;
}

interface AdminSessionTokens {
  access_token: string;
  refresh_token: string;
}

interface ImpersonationState {
  profile: ImpersonationProfile;
  adminUserId: string;
  adminName: string;
  logId: string;
  adminSession: AdminSessionTokens;
  startedAt: string;
}

const BLOCKED_ACTIONS = [
  "password_change",
  "email_change",
  "2fa_reset",
  "2fa_disable",
  "withdrawal_submit",
  "fiat_withdrawal_submit",
  "order_place",
  "p2p_action",
  "api_key_manage",
  "account_delete",
  "kyc_modify",
  "security_settings",
] as const;

interface ImpersonationContextType {
  isImpersonating: boolean;
  impersonatedUserId: string | null;
  impersonatedProfile: ImpersonationProfile | null;
  adminUserId: string | null;
  adminName: string | null;
  logId: string | null;
  isStopping: boolean;
  startImpersonation: (userId: string, reason?: string) => Promise<boolean>;
  stopImpersonation: () => Promise<void>;
  effectiveUserId: (realUserId: string | null) => string | null;
  isActionBlocked: (action: string) => boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType>({
  isImpersonating: false,
  impersonatedUserId: null,
  impersonatedProfile: null,
  adminUserId: null,
  adminName: null,
  logId: null,
  isStopping: false,
  startImpersonation: async () => false,
  stopImpersonation: async () => {},
  effectiveUserId: (id) => id,
  isActionBlocked: () => false,
});

const STORAGE_KEY = "acex_impersonation";

function loadState(): ImpersonationState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationState;
    if (!parsed?.profile?.user_id || !parsed?.adminSession?.refresh_token) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveState(state: ImpersonationState) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function clearState() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [state, setState] = useState<ImpersonationState | null>(loadState);
  const [isStopping, setIsStopping] = useState(false);

  // Keep in sync if another part of the app wrote the key (e.g. after reload).
  useEffect(() => {
    const saved = loadState();
    setState((prev) => (prev?.logId === saved?.logId ? prev : saved));
  }, [user?.id]);

  const startImpersonation = useCallback(
    async (userId: string, reason?: string): Promise<boolean> => {
      if (loadState()) {
        toast({
          title: "Already impersonating",
          description: "Exit current impersonation first.",
          variant: "destructive",
        });
        return false;
      }

      const { data: sessionData } = await supabase.auth.getSession();
      const adminSession = sessionData?.session;
      if (!adminSession?.access_token || !adminSession.refresh_token) {
        toast({ title: "Not authenticated", variant: "destructive" });
        return false;
      }
      const adminUserId = adminSession.user.id;

      const { data, error } = await supabase.functions.invoke("admin-impersonate", {
        body: { action: "start", target_user_id: userId, reason: reason || null },
      });

      const payload = data as
        | {
            success?: boolean;
            error?: string;
            log_id?: string;
            token_hash?: string;
            admin_name?: string;
            profile?: ImpersonationProfile;
          }
        | null;

      if (error || !payload?.success || !payload.token_hash || !payload.log_id) {
        const description = await extractEdgeFunctionError(
          error,
          payload,
          "We could not open this account. Please try again.",
        );
        toast({
          title: "Failed to start impersonation",
          description,
          variant: "destructive",
        });
        return false;
      }

      // Swap the browser into a real session for the target user so every
      // query in the app returns that user's own data under RLS.
      const { error: verifyError } = await supabase.auth.verifyOtp({
        type: "magiclink",
        token_hash: payload.token_hash,
      });

      if (verifyError) {
        // Roll back the log row so the audit trail isn't left "active".
        await supabase.functions.invoke("admin-impersonate", {
          body: { action: "stop", log_id: payload.log_id },
        });
        toast({
          title: "Failed to start impersonation",
          description: verifyError.message,
          variant: "destructive",
        });
        return false;
      }

      const newState: ImpersonationState = {
        profile: payload.profile ?? { user_id: userId, full_name: null },
        adminUserId,
        adminName: payload.admin_name || "Admin",
        logId: payload.log_id,
        adminSession: {
          access_token: adminSession.access_token,
          refresh_token: adminSession.refresh_token,
        },
        startedAt: new Date().toISOString(),
      };

      saveState(newState);
      setState(newState);
      // Drop every cached admin-scoped query so nothing leaks into the user view.
      queryClient.clear();

      return true;
    },
    [queryClient],
  );

  const stopImpersonation = useCallback(async () => {
    const current = state ?? loadState();
    if (!current) return;

    setIsStopping(true);
    try {
      await supabase.functions.invoke("admin-impersonate", {
        body: { action: "stop", log_id: current.logId },
      });

      const { error } = await supabase.auth.setSession({
        access_token: current.adminSession.access_token,
        refresh_token: current.adminSession.refresh_token,
      });

      clearState();
      setState(null);
      queryClient.clear();

      if (error) {
        toast({
          title: "Admin session expired",
          description: "Please sign in again to continue.",
          variant: "destructive",
        });
        await supabase.auth.signOut();
        window.location.href = "/admin/login";
      }
    } finally {
      setIsStopping(false);
    }
  }, [state, queryClient]);

  const effectiveUserId = useCallback(
    (realUserId: string | null) => state?.profile.user_id ?? realUserId,
    [state],
  );

  const isActionBlocked = useCallback(
    (action: string) => {
      if (!state) return false;
      return (BLOCKED_ACTIONS as readonly string[]).includes(action);
    },
    [state],
  );

  return (
    <ImpersonationContext.Provider
      value={{
        isImpersonating: !!state,
        impersonatedUserId: state?.profile.user_id ?? null,
        impersonatedProfile: state?.profile ?? null,
        adminUserId: state?.adminUserId ?? null,
        adminName: state?.adminName ?? null,
        logId: state?.logId ?? null,
        isStopping,
        startImpersonation,
        stopImpersonation,
        effectiveUserId,
        isActionBlocked,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
}

export const useImpersonation = () => useContext(ImpersonationContext);
